import { SystemSettingsService } from './systemSettingsService';

const DEBUG_MODE_KEY = 'debug_mode_enabled';
const DEBUG_STORAGE_KEY = 'app_debug_mode';

export class DebugService {
  /**
   * Check if debug mode is enabled
   * Checks localStorage first (for immediate effect), then database
   */
  static async isDebugModeEnabled(): Promise<boolean> {
    // Check localStorage first for immediate effect
    const localDebug = localStorage.getItem(DEBUG_STORAGE_KEY);
    if (localDebug !== null) {
      return localDebug === 'true';
    }

    // Fallback to database setting
    try {
      const setting = await SystemSettingsService.getSetting(DEBUG_MODE_KEY);
      const enabled = setting === true || setting === 'true';
      
      // Sync to localStorage
      localStorage.setItem(DEBUG_STORAGE_KEY, enabled.toString());
      
      return enabled;
    } catch (error) {
      console.error('Error checking debug mode:', error);
      return false;
    }
  }

  /**
   * Get debug mode status synchronously (from localStorage)
   */
  static isDebugModeEnabledSync(): boolean {
    const localDebug = localStorage.getItem(DEBUG_STORAGE_KEY);
    if (localDebug !== null) {
      return localDebug === 'true';
    }
    return false;
  }

  /**
   * Set debug mode
   */
  static async setDebugMode(enabled: boolean): Promise<void> {
    try {
      // Update database
      await SystemSettingsService.updateSetting(
        DEBUG_MODE_KEY,
        enabled,
        'Enable debug mode for detailed logging and debug panels'
      );

      // Update localStorage for immediate effect
      localStorage.setItem(DEBUG_STORAGE_KEY, enabled.toString());

      // Log the change
      if (enabled) {
        console.log('%c🔍 Debug Mode Enabled', 'color: #10b981; font-weight: bold; font-size: 14px;');
        console.log('Debug logging and panels are now active.');
      } else {
        console.log('%c🔍 Debug Mode Disabled', 'color: #ef4444; font-weight: bold; font-size: 14px;');
      }
    } catch (error) {
      console.error('Error setting debug mode:', error);
      throw error;
    }
  }

  /**
   * Conditional debug logging
   */
  static logDebug(...args: any[]): void {
    if (this.isDebugModeEnabledSync()) {
      console.log('[DEBUG]', ...args);
    }
  }

  /**
   * Conditional debug error logging
   */
  static logError(...args: any[]): void {
    if (this.isDebugModeEnabledSync()) {
      console.error('[DEBUG ERROR]', ...args);
    }
  }

  /**
   * Conditional debug warning logging
   */
  static logWarning(...args: any[]): void {
    if (this.isDebugModeEnabledSync()) {
      console.warn('[DEBUG WARNING]', ...args);
    }
  }

  /**
   * Log performance metric
   */
  static logPerformance(label: string, startTime: number): void {
    if (this.isDebugModeEnabledSync()) {
      const duration = performance.now() - startTime;
      console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Get debug info object
   */
  static getDebugInfo(): Record<string, any> {
    return {
      debugMode: this.isDebugModeEnabledSync(),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      localStorage: {
        size: new Blob([JSON.stringify(localStorage)]).size,
        keys: Object.keys(localStorage)
      }
    };
  }
}

